// DS row 120 ConfigTable: an Admin configuration list (O-21; his TA Admin folds into the Admin, DC-228): *Statuses ·
// Reason lists · Taxonomies · Thresholds · Reminders · Templates · Segments · Strategies*. A dense table of the values
// with an *Effective version* mono column and the *Current* pill on the active version (pending Q-E16); the *Show previous versions* toggle adds the earlier
// versions (*Previous version*, pending Q-E16); each row of the current version carries an icon button opening the
// menu *Edit · View history*; the page action is *New version*. mode="view" (a seat that is not Admin): the values
// and the read-only toggle only, no menus, no page action. `blocked` dashes the page action and the Edit items with his
// sentence. Each row's menu button is described by its row's name cell, so the identical names differ.

import { useId, type ReactNode } from "react";
import { Button, MenuButton, Pill, Table, Td, Th, Toggle, Tr, SURFACE_LABELS, type Blocked } from "../core";
import { cx } from "../lib/cx";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { G6_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export interface ConfigRow {
  key: string;
  /** The value's name (a status word, a reason, a threshold's name). */
  name: ReactNode;
  /** The configured value, when the list has one beside the name (a number of days, a template line). */
  value?: ReactNode | undefined;
  /** The effective version (*v3*), in mono. */
  version: string;
  /** The active version: the *Current* pill. */
  current?: boolean | undefined;
  /** An earlier version: shown only with *Show previous versions*, tagged *Previous version*. */
  previous?: boolean | undefined;
  hs?: Hs | undefined;
}

export interface ConfigTableProps {
  /** The list's name (*Statuses*, *Reason lists* …): the first column's head and the table's name. */
  label: string;
  /** The value column's head, when rows carry a value. */
  valueHead?: string | undefined;
  rows: readonly ConfigRow[];
  showPrevious: boolean;
  onShowPreviousChange?: ((show: boolean) => void) | undefined;
  onEdit?: ((key: string) => void) | undefined;
  onViewHistory?: ((key: string) => void) | undefined;
  onNewVersion?: (() => void) | undefined;
  /** A rule blocks changes (Edit and New version), with his sentence. */
  blocked?: Blocked | undefined;
  /** "view": values only (every seat but Admin). */
  mode?: "edit" | "view" | undefined;
  /** Skeleton rows while the list loads. */
  loading?: boolean | undefined;
  /** The page action's hotspot. */
  hs?: Hs | undefined;
  can?: boolean | undefined;
  className?: string | undefined;
}

export function ConfigTable({
  label, valueHead, rows, showPrevious, onShowPreviousChange, onEdit, onViewHistory, onNewVersion, blocked, mode, loading, hs, can, className,
}: ConfigTableProps) {
  const toggleId = useId();
  const rowId = (row: ConfigRow) => `${toggleId}-n-${row.key}-${row.version}`;
  if (can === false) return null;
  const edit = mode !== "view";
  const hasValue = valueHead !== undefined;
  const drawn = rows.filter((row) => showPrevious || !row.previous);
  const head = (
    <Tr>
      <Th>{label}</Th>
      {hasValue ? <Th>{valueHead}</Th> : null}
      <Th>{G6_LABELS.effectiveVersion}</Th>
      {edit ? <Th align="r"><VisuallyHidden>{G6_LABELS.editViewHistory}</VisuallyHidden></Th> : null}
    </Tr>
  );
  return (
    <div className={cx("mc-cfg", className)}>
      <div className="mc-cfg-bar">
        <Toggle id={toggleId} label={SURFACE_LABELS.showPreviousVersions} checked={showPrevious} onChange={onShowPreviousChange} />
        {edit && onNewVersion ? (
          <Button tone="primary" sm icon="plus" onClick={onNewVersion} blocked={blocked} hs={hs}>{G6_LABELS.newVersion}</Button>
        ) : null}
      </div>
      <Table dense stickyHead label={label} head={head} loading={loading ? 3 : undefined}>
        {drawn.map((row) => (
          <Tr key={`${row.key}-${row.version}`} className={row.previous ? "mc-cfg-prev" : undefined}>
            <Td label={label}><span id={rowId(row)}>{row.name}</span></Td>
            {hasValue ? <Td label={valueHead}>{row.value}</Td> : null}
            <Td label={G6_LABELS.effectiveVersion} nowrap>
              <span className="mc-cfg-v">
                <span className="mc-mono">{row.version}</span>
                {row.current ? <Pill tone="info">{G6_LABELS.current}</Pill> : null}
                {row.previous ? <Pill>{G6_LABELS.previousVersion}</Pill> : null}
              </span>
            </Td>
            {edit ? (
              <Td align="r">
                {row.previous ? null : (
                  <MenuButton
                    iconOnly
                    sm
                    align="end"
                    label={G6_LABELS.editViewHistory}
                    hs={row.hs}
                    describedBy={rowId(row)}
                    items={[
                      { label: SURFACE_LABELS.edit, icon: "note", onSelect: () => onEdit?.(row.key), blocked },
                      { label: SURFACE_LABELS.viewHistory, icon: "clock", onSelect: () => onViewHistory?.(row.key) },
                    ]}
                  />
                )}
              </Td>
            ) : null}
          </Tr>
        ))}
      </Table>
    </div>
  );
}
